// Appearance.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Area,
  useTheme,
  Radio,
  Stack,
  Typography,
} from '@ublend-npm/aulaui-core';
import { useMediaQuery } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { THEME_SWITCHING } from '@core/constants/actions';
import pushRoute from '@app/utils/routing';

const THEME_KEY = 'theme';

const Appearance = ({ route }: any) => {
  const { breakpoints, palette } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up('md'));
  const dispatch = useDispatch();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const ldClient = useLDClient();
  const flags = ldClient?.allFlags();
  // Redirect to account details if feature flag is disabled
  useEffect(() => {
    if (!flags?.WebAppFtAppearance202505) {
      pushRoute('/profile/account-details');
    }
  }, [flags?.WebAppFtAppearance202505]);

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    headingRef.current?.focus();
  }, []);

  // Don't render if feature flag is disabled
  if (!flags?.WebAppFtAppearance202505) {
    return null;
  }

  const getInitialTheme = () => {
    const storedTheme = localStorage.getItem(THEME_KEY);
    if (!storedTheme || !['light', 'dark', 'system'].includes(storedTheme)) {
      return 'system';
    }
    return storedTheme;
  };

  const [selectedTheme, setSelectedTheme] = useState(getInitialTheme());

  useEffect(() => {
    const stored = localStorage.getItem(THEME_KEY);

    setSelectedTheme(
      stored && ['light', 'dark', 'system'].includes(stored) ? stored : 'system'
    );

    const resolveAndDispatchTheme = () => {
      if (!stored || stored === 'system') {
        localStorage.setItem(THEME_KEY, 'system');
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
          .matches
          ? 'dark'
          : 'light';

        dispatch({ type: THEME_SWITCHING, theme: systemTheme });
      } else {
        dispatch({ type: THEME_SWITCHING, theme: stored });
      }
    };

    const handleSystemThemeChange = () => {
      // Only respond to system theme changes if user has selected "system" mode
      const currentSelection = localStorage.getItem(THEME_KEY);

      if (currentSelection === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
          .matches
          ? 'dark'
          : 'light';

        dispatch({ type: THEME_SWITCHING, theme: systemTheme });
      }
    };

    resolveAndDispatchTheme();

    // Always listen for system theme changes, regardless of current selection
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () =>
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [dispatch]);

  const handleThemeChange = (value: string) => {
    setSelectedTheme(value);
    localStorage.setItem(THEME_KEY, value);

    if (value === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';
      dispatch({ type: THEME_SWITCHING, theme: systemTheme });
    } else {
      dispatch({ type: THEME_SWITCHING, theme: value });
    }
  };

  return (
    <Box
      height="100vh"
      bgcolor={palette.background.default}
      id="content-container"
    >
      <Grid container maxWidth="75rem" margin="0 auto">
        {isDesktop && (
          <Grid size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
            {route.profileMenu()}
          </Grid>
        )}
        <Grid
          size={{ xs: 12, sm: 12, md: 9, lg: 9 }}
          sx={{
            padding: {
              xs: '24px 16px',
              sm: '24px',
              md: '40px 24px',
              lg: '40px 24px',
            },
          }}
        >
          <Area sx={{ borderRadius: '.5rem', padding: '32px 24px' }}>
            <Typography
              id="appearance-theme-heading"
              ref={headingRef}
              variant="h6"
              fontWeight={500}
              fontSize="24px"
              mb="24px"
              color={palette.text.primary}
              tabIndex={0}
            >
              Appearance preferences
            </Typography>

            <Box
              sx={{
                height: '1px',
                width: '100%',
                backgroundColor: palette.divider,
                marginBottom: '24px',
              }}
            />

            <Typography
              fontSize="16px"
              color={
                palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.87)'
                  : 'rgba(255, 255, 255, 1)'
              }
              mb="24px"
              tabIndex={0}
            >
              Choose if Aula’s appearance should be light or dark, or follow
              your device settings.
            </Typography>

            <Stack
              direction="column"
              gap="8px"
              sx={{ px: '11px' }}
              role="radiogroup"
              aria-labelledby="appearance-theme-heading"
            >
              {['light', 'dark', 'system'].map((themeOption) => (
                <Box
                  key={themeOption}
                  display="flex"
                  alignItems="center"
                  bottom="32px"
                  gap="8px"
                  height="42px"
                >
                  <Radio
                    value={themeOption}
                    aria-label={themeOption}
                    role="radio"
                    aria-checked={selectedTheme === themeOption}
                    checked={selectedTheme === themeOption}
                    onChange={() => handleThemeChange(themeOption)}
                    tabIndex={0}
                  />
                  <Typography
                    variant="body1"
                    textTransform="capitalize"
                    tabIndex={0}
                  >
                    {themeOption}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Area>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Appearance;
